
export interface IGenericListResponse<T> {
  limit: number;
  skip: number;
  total: number;
  items: T[];
}

import { Injectable           } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
  HttpParams                  } from '@angular/common/http';
import { Auth0Provider        } from './auth0.provider';
import {
  ToastController,
  AlertController             } from 'ionic-angular';
import { Subject              } from 'rxjs/Subject';
import { get                  } from 'lodash';

import 'rxjs/add/operator/catch';
import { IUser } from '../../typings/timetracker/timetracker';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

export const API_ENDPOINT = 'https://tijdregistratie.visveilingurk.nl/api';
// export const API_ENDPOINT = 'http://192.168.1.32:8888';
// export const API_ENDPOINT = 'http://192.168.1.129:8888';

@Injectable()
export class TimeTrackerBaseApi {

  private loginRequiredSubject:Subject<any> = new Subject<any>();
  private currentUserSubject:BehaviorSubject<IUser> = new BehaviorSubject(null);

  getErrorMessage( error: any) {
    const message = get( error, 'error.message' );
    if ( message ) {
      return message;
    } else {
      return JSON.stringify( error, null, ' ' );
    }
  }

  /**
   * On error show the error message. On 401, go to the login page
   * @param error
   */
  genericErrorHandler( error ) {
    console.error( '[TIMETRACKER] Error:', error, JSON.stringify( error ) );

    if ( error instanceof HttpErrorResponse) {
      if ( error.status === 401) {
        this.loginRequiredSubject.next( true );
      } else if ( error.status === 403 ) {
        this.showAlert( 'Geen toegang', 'Je account is nog niet geactiveerd. Op dit moment heb je nog geen toegang tot het tijdregistratie systeem. Neem contact op met je leidinggevende of de salarisadministratie om je account te activeren' )
      } else {
        this.showToast( this.getErrorMessage( error ) );
      }
    } else {
      this.showToast( this.getErrorMessage( error ) );
    }
  }

  doLoginRequired() {
    return this.loginRequiredSubject.asObservable();
  }

  selectCurrentUser() {
    return this.currentUserSubject.asObservable();
  }

  showAlert( title: string = 'Melding', message: string = 'Niets te melden' ) {
    if ( title && message ) {
      let alert = this.$alertCtrl.create({
        title: title,
        subTitle: message,
        buttons: ['Sluiten']
      });
      alert.present();
    }
  }
  /**
   * Show toast messages
   */
  showToast( message: string ) {
    let toast = this.$toastCtrl.create({
      message: message,
      duration: 3000
    });
    toast.present();
  }

  /**
   * Utility method to check if there is an active session available
   */
  isLoggedIn(): Promise<boolean> {
    return new Promise( ( resolve, reject ) => {
      this.genericGetCall( '/auth' )
      .subscribe(
        ( response: IUser ) => {
          this.currentUserSubject.next( response );
          console.log( '[SESSION] Check if there is a active session available', response );
          resolve( true );
        }
      );
    } );
  }

  // /**
  //  * Utility method that delete empty field from an object
  //  *
  //  * @param {any} obj Object to remove empty field for
  //  */
  // removeEmptyFields( obj = {} ) {
  //   Object.keys(obj).forEach( function( key ) {
  //     if ( obj[ key ] && typeof obj[ key ] === 'object' ) {
  //       this.removeEmptyFields( obj[ key ] );
  //     } else if ( obj[ key ] == null ) {
  //       delete obj[ key ];
  //     }
  //   } );
  //   return obj;
  // }

  /**
   * Performs a (paginatable) list XHR call
   * @param {string} url            The URL to call
   * @param {number} limit          The maximum number of list items to retrieve
   * @param {number} skip           The offset of the list to retrieve
   * @param {string} sortField      The field to sort on
   * @param {string} sortDirection  The sort direction. Possibile values are 'DESC' and 'ASC'
   */
  genericListCall( url: string, limit: number, skip: number, sortField: string = 'created_at', sortDirection: string = 'DESC' ) {
    const token = this.$auth0Provider.token( 'id' );
    const params = new HttpParams()
    .set( 'limit',          `${limit}` )
    .set( 'skip',           `${skip}` )
    .set( 'sortField',      sortField )
    .set( 'sortDirection',  sortDirection );
    return this.$http.get( API_ENDPOINT + url, {
      headers: new HttpHeaders().set( 'Authorization', `Bearer ${token}` ),
      params: params
    } )
    // .do( ( payload ) => console.log( '==> payload', payload ) )
    .catch( ( error: HttpErrorResponse ) => {
      this.genericErrorHandler( error );
      throw error;
    } );
  }

  /**
   * Performs a HTTP GET request
   *
   * @param {string} url The URL to call
   */
  genericGetCall( url: string) {
    const token = this.$auth0Provider.token( 'id' );
    return this.$http.get( API_ENDPOINT + url, {
      headers: new HttpHeaders().set( 'Authorization', `Bearer ${token}` ),
    } ).catch( ( error ) => {
      this.genericErrorHandler( error );
      throw error;
    } );
  }

  /**
   * Performs a HTTP POST request
   *
   * @param {string} url  The URL to call
   * @param {any}    data The data to send with the XHR request
   */
  genericPostCall( url: string, data?: any ) {
    const token = this.$auth0Provider.token( 'id' );
    return this.$http.post( API_ENDPOINT + url, data, {
      headers: new HttpHeaders().set( 'Authorization', `Bearer ${token}` ),
    } ).catch( ( error ) => {
      this.genericErrorHandler( error );
      throw error;
    } );
  }

  /**
   * Performs a HTTP PUT request
   *
   * @param {string} url  The URL to call
   * @param {any}    data The data to send with the XHR request
   */
  genericPutCall( url: string, data?: any ) {
    const token = this.$auth0Provider.token( 'id' );
    return this.$http.put( API_ENDPOINT + url, data, {
      headers: new HttpHeaders().set( 'Authorization', `Bearer ${token}` ),
    } ).catch( ( error ) => {
      this.genericErrorHandler( error );
      throw error;
    } );
  }

  /**
   * Performs a HTTP PUT request
   *
   * @param {string} url  The URL to call
   * @param {any}    data The data to send with the XHR request
   */
  genericPatchCall( url: string, data?: any ) {
    const token = this.$auth0Provider.token( 'id' );
    return this.$http.patch( API_ENDPOINT + url, data, {
      headers: new HttpHeaders().set( 'Authorization', `Bearer ${token}` ),
    } ).catch( ( error ) => {
      this.genericErrorHandler( error );
      throw error;
    } );
  }

  /**
   * Performs a HTTP DELETE request
   *
   * @param {string} url  The URL to call
   */
  genericDeleteCall( url: string, data?: any ) {
    const token = this.$auth0Provider.token( 'id' );

    const options: any = {
      headers: new HttpHeaders().set( 'Authorization', `Bearer ${token}` )
    };

    if ( data ) {
      options.body = data;
    }

    return this.$http.delete( API_ENDPOINT + url, options ).catch( ( error ) => {
      this.genericErrorHandler( error );
      throw error;
    } );
  }

  constructor(
    private $http: HttpClient,
    private $toastCtrl: ToastController,
    private $auth0Provider: Auth0Provider,
    private $alertCtrl: AlertController ) {}
}




// WEBPACK FOOTER //
// ./src/providers/generic.service.ts